import { computed } from 'vue';
import store from '@/store';

/**
 * Full display name of the authenticated admin user.
 * Falls back to the email when the user has no name fields.
 * @param {object|null} user
 * @returns {string}
 */
export function displayNameOf(user) {
    if (!user) return '';
    const parts = [user.name, user.surnames]
        .filter(part => !!part && String(part).trim() !== '');
    const fullName = parts.join(' ').trim();
    return fullName || user.email || '';
}

/**
 * Initials for the sidebar avatar (name + surnames first letters).
 * Falls back to the email first letter, then to '?'.
 * @param {object|null} user
 * @returns {string}
 */
export function initialsOf(user) {
    if (!user) return '';
    const firstLetters = [user.name, user.surnames]
        .map(part => String(part || '').trim().split(/\s+/).filter(Boolean)[0] || '')
        .map(word => word.charAt(0))
        .join('');
    if (firstLetters) return firstLetters.toUpperCase().slice(0, 2);
    const email = String(user.email || '').trim();
    return email ? email.charAt(0).toUpperCase() : '?';
}

/**
 * Human-readable label or empty string. Numeric values are identifiers, not
 * labels: the API exposes `role` (id) and sometimes `roles[].name` without a
 * name, and printing "3" in the card is never desired.
 * @param {*} value
 * @returns {string}
 */
export function humanLabelOf(value) {
    const text = String(value === null || value === undefined ? '' : value).trim();
    if (!text || /^\d+$/.test(text)) return '';
    return text;
}

/**
 * Human-readable role(s) from the authenticated user `roles` relation.
 * Legacy payloads without `roles` fall back to `tipo`; numeric ids never
 * produce a label.
 * @param {object|null} user
 * @returns {string}
 */
export function roleLabelOf(user) {
    if (!user) return '';
    const roles = Array.isArray(user.roles) ? user.roles : [];
    const names = roles
        .map(role => (typeof role === 'string' ? role : role && role.name))
        .map(humanLabelOf)
        .filter(Boolean);
    if (names.length) return names.join(', ');

    const tipo = humanLabelOf(user.tipo);
    if (!tipo || tipo === 'user') return '';
    if (tipo === 'compania') return 'Compañía';
    return tipo.charAt(0).toUpperCase() + tipo.slice(1);
}

/**
 * Authenticated user identity for the admin shell (sidebar user card).
 * `loading` is true until the boot CHECK (refresh token) hydrates `getUser`.
 */
export function useAdminUser() {
    const user = computed(() => store.getters.getUser);
    const loading = computed(() => !user.value);
    const fullName = computed(() => displayNameOf(user.value));
    const email = computed(() => (user.value && user.value.email) || '');
    const initials = computed(() => initialsOf(user.value));
    const role = computed(() => roleLabelOf(user.value));

    return { user, loading, fullName, email, initials, role };
}
